import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { UserService } from '../../core/services/user.service';
import { NotificationService } from '../../core/services/notification.service';
import { ConfirmationService } from '../../core/services/confirmation.service';
import { passwordValidator } from '../../core/validators/password.validator';

@Component({
    selector: 'app-reset-email',
    templateUrl: './reset-email.component.html',
    styleUrl: './reset-email.component.scss',
    standalone: false
})
export class ResetEmailComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  showNewPassword = false;
  showConfirmPassword = false;
  submitted = false;
  loading = false;
  invalidLink = false;
  resetError = false;
  resendLoading = false;
  token = '';
  email = '';

  private translate = inject(TranslateService);

  constructor(
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService,
    private notificationService: NotificationService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    // Get token and email from query parameters
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || '';
      this.email = params['email'] || '';

      if (!this.token || !this.email) {
        this.invalidLink = true;
      }
    });

    this.resetPasswordForm = this.formBuilder.group({
      newPassword: ['', [Validators.required, passwordValidator()]],
      confirmPassword: ['', [Validators.required]]
    }, {
      validators: this.passwordMatchValidator
    });
  }

  passwordMatchValidator(form: AbstractControl): Record<string, unknown> | null {
    const newPassword = form.get('newPassword');
    const confirmPassword = form.get('confirmPassword');

    if (!newPassword || !confirmPassword) return null;

    if (newPassword.value && confirmPassword.value && newPassword.value !== confirmPassword.value) {
      return { passwordMismatch: true };
    }
    return null;
  }

  get newPassword(): AbstractControl<string, string> | null { return this.resetPasswordForm.get('newPassword'); }
  get confirmPassword(): AbstractControl<string, string> | null { return this.resetPasswordForm.get('confirmPassword'); }

  get passwordsMatch(): boolean {
    return !!(this.newPassword?.value && this.confirmPassword?.value &&
           this.newPassword.value === this.confirmPassword.value);
  }

  get hasPasswordCriteriaError(): boolean {
    if (!this.passwordsMatch) {
      return false;
    }
    const errors = this.newPassword?.errors;
    return !!(errors?.['passwordTooShort'] || errors?.['passwordNoLetter'] || errors?.['passwordNoNumber']);
  }

  toggleNewPasswordVisibility(): void {
    this.showNewPassword = !this.showNewPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    this.submitted = true;
    if (this.resetPasswordForm.invalid) {
      return;
    }

    this.loading = true;
    const formValue = this.resetPasswordForm.value;

    const data = {
      token: this.token,
      email: this.email,
      password: formValue.newPassword,
      password_confirmation: formValue.confirmPassword
    };

    this.userService.resetPassword(data).subscribe({
      next: () => {
        this.loading = false;
        this.confirmationService.swalSuccess(this.translate.instant('AUTH.RESET_PASSWORD.SUCCESS'));
        this.router.navigate(['/account/login']);
      },
      error: () => {
        this.loading = false;
        this.resetError = true;
      }
    });
  }

  resendResetLink(): void {
    if (!this.email || this.resendLoading) {
      return;
    }

    this.resendLoading = true;
    this.resetError = false;

    const data = { email: this.email };

    this.userService.forgetPassword(data).subscribe({
      next: () => {
        this.resendLoading = false;
        this.confirmationService.swalSuccess(this.translate.instant('AUTH.RESET_PASSWORD.RESEND_SUCCESS'));
      },
      error: () => {
        this.resendLoading = false;
        this.confirmationService.swalError(this.translate.instant('AUTH.RESET_PASSWORD.RESEND_FAILED'));
      }
    });
  }
}
